import React, { Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import MainRoutes from "./Navigation/MainRoutes";
import useVerifyUser from "./Helpers/customHooks/useVerifyUser";
import useNetworkStatus from "./Helpers/customHooks/useNetworkStatus";
import PageLoader from "./Components/Loaders/PageLoader";
import { PrivateRoutes } from "./Navigation/PrivateRoutes";
import { useDispatch, useSelector } from "react-redux";
import { Toaster } from "react-hot-toast";
import { actionCreators } from "./Redux";
import ProtectedRoute from "./Navigation/ProtectedRoute";
import { MODULE_LABELS } from "./Components/Sidebar/AllModules";
import ScrollToTop from "./Components/ScrollToTop/ScrollToTop";
import SidebarSkeleton from "./Components/Loaders/Skeleton/SidebarSkeleton";
import { ACTIVE_VERSION } from "./Api/base";
import { versionRelatedEndPoints } from "./Api/Endpoints";
import VersionApiCall from "./Api/VersionApiCall";
import { startApiCall } from "./Helpers/globalFunctions";
import ComplaintsWrapper from "./Pages/Complaints/ComplaintsWrapper";

const Navbar = React.lazy(() => import("./Components/Header/Navbar"));
const Sidebar = React.lazy(() => import("./Components/Sidebar/Sidebar"));
const Home = React.lazy(() => import("./Pages/Home/Home"));
const Login = React.lazy(() => import("./Pages/Authentication/Login/Login"));
const Signup = React.lazy(() => import("./Pages/Authentication/Signup/Signup"));
const ForgotPassword = React.lazy(() =>
  import("./Pages/Authentication/ForgotPassword/ForgotPasswordForm")
);
const Profile = React.lazy(() => import("./Pages//Profile/Profile"));
const Activity = React.lazy(() =>
  import("./Pages/SocietyManagement/Levels/Activity/Activity")
);
const Levels = React.lazy(() =>
  import("./Pages/SocietyManagement/Levels/Levels")
);
const SocietyManagement = React.lazy(() =>
  import("./Pages/SocietyManagement/SocietyManage/SocietyManage")
);
const SocietyRequest = React.lazy(() =>
  import(
    "./Pages/SocietyManagement/SocietyManage/SocietyRequests/SocietyRequests"
  )
);
const LevelDetails = React.lazy(() =>
  import("./Pages/SocietyManagement/Levels/LevelDetails/LevelDetails")
);
const LevelData = React.lazy(() =>
  import("./Pages/SocietyManagement/Levels/LevelData/LevelData")
);
const LevelDataDetails = React.lazy(() =>
  import(
    "./Pages/SocietyManagement/Levels/LevelData/LevelDataDetails/LevelDataDetails"
  )
);
const AddAdvanceVendorSetting = React.lazy(() =>
  import(
    "./Pages/SocietyManagement/Levels/LevelData/AdvanceVendorSetting/AddAdvanceVendorSetting"
  )
);
const AllAdvanceVendorSetting = React.lazy(() =>
  import(
    "./Pages/SocietyManagement/Levels/LevelData/AdvanceVendorSetting/AllAdvanceVendorSettings"
  )
);
const Grouping = React.lazy(() => import("./Pages/Settings/Grouping/Grouping"));

const GroupingDetails = React.lazy(() =>
  import("./Pages/Settings/GroupingDetails/GroupingDetails")
);
const Users = React.lazy(() => import("./Pages/People/Users/Users"));
const UserDetails = React.lazy(() =>
  import("./Pages/People/Users/UserDetails/UserDetails")
);
const UserRequest = React.lazy(() =>
  import("./Pages/People/Users/UserRequest/UserRequest")
);
const Role = React.lazy(() => import("./Pages/Settings/Role/Role"));
const RoleDetails = React.lazy(() =>
  import("./Pages/Settings/Role/RoleDetails/RoleDetails")
);
const HelpDesk = React.lazy(() =>
  import("./Pages/Complaints/Helpdesk/Helpdesk")
);

const AllComplaints = React.lazy(() =>
  import("./Pages/Complaints/AllComplaints/AllComplaints/AllComplaints")
);

const ComplainDetails = React.lazy(() =>
  import("./Pages/Complaints/AllComplaints/ComplaintDetails/ComplaintDetails")
);

const ComplaintConfiguration = React.lazy(() =>
  import("./Pages/Complaints/ComplaintConfiguration/ComplaintConfiguration")
);
const EscalationDetails = React.lazy(() =>
  import(
    "./Pages/Complaints/ComplaintConfiguration/EscalationMatrix/EscalationDetails/EscalationDetails"
  )
);
const ComplaintDeadlineApproval = React.lazy(() =>
  import("./Pages/Complaints/DeadlineApproval/DeadlineApproval")
);
const ComplaintRequestApproval = React.lazy(() =>
  import("./Pages/Complaints/ComplaintRequestApproval/ComplaintApproval")
);
const ComplaintWithoutRating = React.lazy(() =>
  import("./Pages/Complaints/AllComplaints/ComplaintWithoutRating/ComplaintWithoutRatingPage")
);
const VendorRating = React.lazy(() =>
  import("./Pages/Complaints/VendorRating/VendorRating")
);

const Unauthorized = React.lazy(() =>
  import("./Components/Unauthorized/Unauthorized")
);

//MIS Dashboard
const MisComplaintDashboard = React.lazy(() =>
  import("./Pages/MisPivot/ComplaintDashboard")
);
const MisLocationAnalytics = React.lazy(() =>
  import("./Pages/MisPivot/LocationAnalytics")
);
const MisAssignedReport = React.lazy(() =>
  import("./Pages/MisPivot/AssignedReport")
);
const MisClosedReport = React.lazy(() =>
  import("./Pages/MisPivot/ClosedReport")
);
const MisCancelledReport = React.lazy(() =>
  import("./Pages/MisPivot/CancelledReport")
);
const MisVendorAnalytics = React.lazy(() =>
  import("./Pages/MisPivot/VendorAnalytics")
);

const MisSettings = React.lazy(() =>
  import("./Pages/MisPivot/MisSettings")
);
const MaintenancePage = React.lazy(() =>
  import("./Pages/Maintenance/Maintenance")
);

//Notice

const Notice = React.lazy(() => import("./Pages/Communication/Notice/Notice"));
const NoticeDetails = React.lazy(() =>
  import("./Pages/Communication/Notice/NoticeDetails")
);

//All Logs

const AllLogs = React.lazy(() => import("./Pages/AllLogs/AllLogs"));

const Token = React.lazy(() => import("./Pages/Settings/Token/Token"));

//Help Management
const HelpCenter = React.lazy(() =>
  import("./Pages/HelpManagement/HelpCenter/HelpCenter")
);
const AddFaq = React.lazy(() =>
  import("./Pages/HelpManagement/HelpCenter/AddFaq")
);
const FaqDetails = React.lazy(() =>
  import("./Pages/HelpManagement/HelpCenter/FaqDetails/FaqDetails")
);
//Contact Book
const ContactBook = React.lazy(() =>
  import("./Pages/ContactBook/ContactBook")
);
const ContactBookSubcategory = React.lazy(() =>
  import("./Pages/ContactBook/ContactBookCategory/ContactBookSubcategory/ContactBookSubcategory")
);
const Contact = React.lazy(() =>
  import("./Pages/ContactBook/Contact/Contact")
);

const App = () => {
  const dispatch = useDispatch();
  const [mainLoader, setmainLoader] = useState(true);
  const user = useSelector((state) => state.user);
  const switchedRole = useSelector((state) => state.switchedRole);
  const userHasRoleWithPermission = useSelector(
    (state) => state.userHasRoleWithPermission
  );
  const currentSociety = useSelector((state) => state.switchedSociety);
  const { NetworkStatusComponent, isOnline } = useNetworkStatus();
  /***VERSION STATES */
  const [isMaintainance, setisMaintainance] = useState(false);
  const [isVersionLoader, setisVersionLoader] = useState(false);

  //** SWITCHED ROLE STATICALLY BY DEFAULT FIRST ONE **
  useEffect(() => {
    if (userHasRoleWithPermission && !switchedRole && currentSociety) {
      const role =
        userHasRoleWithPermission?.[currentSociety?._id]?.roles?.["0"] || null;
      dispatch(actionCreators.switchedRole(role));
    }
  }, [userHasRoleWithPermission, currentSociety, dispatch]);

  // Custom hook for verify user
  const isVerifying = useVerifyUser(setmainLoader);

  // ******** VERSION CHECK FOR DASHBOARD **********
  const handleGetVersionForDashboard = async () => {
    startApiCall(null, setisVersionLoader);
    const data = {
      platform: "dashboard",
      projectName: "sociohood_new",
      version: ACTIVE_VERSION,
    };
    const res = await VersionApiCall(
      "post",
      versionRelatedEndPoints.checkActiveVersion,
      data
    );
    if (res?.success) {
      if (res?.version?.version !== ACTIVE_VERSION) {
        console.log("🚷🚷🚷🚷🚷VERISON DID'T MATHCED🚷🚷🚷🚷🚷");
        setisMaintainance(true);
      } else {
        console.log("✅✅✅✅✅VERISON MATHCED✅✅✅✅✅");
        setisMaintainance(res?.version ? res?.version?.isMaintenance : true);
      }
      setisVersionLoader(false);
    } else {
      setisVersionLoader(false);
    }
  };
  // ******** VERSION API CALLED **********
  useEffect(() => {
    handleGetVersionForDashboard();
  }, []);

  if (isVerifying || mainLoader) {
    return <PageLoader />;
  }

  return (
    <div className="wrapper">
      <ScrollToTop />
      <Suspense fallback={<SidebarSkeleton />}>{user && <Sidebar />}</Suspense>
      <div className="wrapper-main">
        {isOnline && (
          <Suspense fallback={<PageLoader />}>{user && <Navbar />}</Suspense>
        )}
        <Suspense fallback={<PageLoader />}>
          {!isOnline && <NetworkStatusComponent />}

          <Routes>
            <Route
              path={MainRoutes.LOGIN}
              element={
                isMaintainance ? (
                  <MaintenancePage />
                ) : user ? (
                  <Navigate to={MainRoutes.SOCIETYMANAGE} />
                ) : (
                  <Login />
                )
              }
            />

            <Route
              path={MainRoutes?.SIGNUP}
              element={isMaintainance ? <MaintenancePage /> : <Signup />}
            />
            <Route
              path={MainRoutes?.FORGOTPASSWORD}
              element={
                isMaintainance ? <MaintenancePage /> : <ForgotPassword />
              }
            />
            {isMaintainance ? (
              <>
                <Route path="*" element={<MaintenancePage />} />
              </>
            ) : (
              <Route element={<PrivateRoutes user={user} />}>
                <Route
                  path={MainRoutes.HOME}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.DASHBOARD}
                    >
                      <Home />
                    </ProtectedRoute>
                  }
                />
                {/* Profile Routes */}
                <Route path={MainRoutes.PROFILE} element={<Profile />} />

                {/* PUBLIC ROUTES SOCIETY MANAGE */}
                <Route
                  path={MainRoutes.SOCIETYMANAGE}
                  element={<SocietyManagement />}
                />
                <Route
                  path={MainRoutes.SOCIETYREQUESTS}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.SOCIETY_REQUESTS}
                    >
                      <SocietyRequest />
                    </ProtectedRoute>
                  }
                />

                {/*All Logs */}
                <Route
                  path={MainRoutes.ALLLOGS}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.SOCIETY_REQUESTS}
                    >
                      <AllLogs />
                    </ProtectedRoute>
                  }
                />
                {/* Level Routes */}
                <Route
                  path={MainRoutes.LEVELS}
                  element={
                    <ProtectedRoute requiredPermission={MODULE_LABELS.LEVEL}>
                      <Levels />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path={`${MainRoutes.LEVELDETAILS}/:id`}
                  element={
                    <ProtectedRoute requiredPermission={MODULE_LABELS.LEVEL}>
                      <LevelDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={MainRoutes.LEVELDATA}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.LEVEL_DATA}
                    >
                      <LevelData />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`${MainRoutes.ADDADVANCEVENDORSETTING}/:name/:id`}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.LEVEL_DATA}
                    >
                      <AddAdvanceVendorSetting />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`${MainRoutes.AllADVANCEVENDORSETTING}/:name/:id`}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.LEVEL_DATA}
                    >
                      <AllAdvanceVendorSetting />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`${MainRoutes.ACTIVITY}/:id/:displayName/:displayValue`}
                  element={<Activity />}
                />
                {/* Group Routes */}
                <Route
                  path={MainRoutes.GROUPING}
                  element={
                    <ProtectedRoute requiredPermission={MODULE_LABELS.GROUPING}>
                      <Grouping />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`${MainRoutes.GROUPINGDETAILS}/:id`}
                  element={
                    <ProtectedRoute requiredPermission={MODULE_LABELS.GROUPING}>
                      <GroupingDetails />
                    </ProtectedRoute>
                  }
                />
                {/* User Routes */}
                <Route
                  path={MainRoutes.USERS}
                  element={
                    <ProtectedRoute requiredPermission={MODULE_LABELS.USERS}>
                      <Users />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={MainRoutes.USERDETAILS}
                  element={
                    <ProtectedRoute requiredPermission={MODULE_LABELS.USERS}>
                      <UserDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={MainRoutes.USERREQUEST}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.USER_REQUESTS}
                    >
                      <UserRequest />
                    </ProtectedRoute>
                  }
                />
                {/* Role Routes */}
                <Route
                  path={MainRoutes.ROLE}
                  element={
                    <ProtectedRoute requiredPermission={MODULE_LABELS.ROLE}>
                      <Role />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`${MainRoutes.ROLEDETAILS}/:id`}
                  element={
                    <ProtectedRoute requiredPermission={MODULE_LABELS.ROLE}>
                      <RoleDetails />
                    </ProtectedRoute>
                  }
                />
                {/* Complaints Help Desk Routes */}
                <Route
                  path={`${MainRoutes.HELPDESK}`}
                  element={
                    <ProtectedRoute requiredPermission={MODULE_LABELS.HELPDESK}>
                      <HelpDesk />
                    </ProtectedRoute>
                  }
                />
                {/*All Complaints Routes - Wrapped with ComplaintsWrapper to maintain filter state */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.ALL_COMPLAINTS}
                    >
                      <ComplaintsWrapper />
                    </ProtectedRoute>
                  }
                >
                  <Route
                    path={MainRoutes.ALLCOMPLAINTS}
                    element={<AllComplaints />}
                  />
                  <Route
                    path={MainRoutes.COMPLAINT_WITHOUT_RATING}
                    element={<ComplaintWithoutRating />}
                  />
                  <Route
                    path={`${MainRoutes.COMPLAINTDETAILS}/:id`}
                    element={<ComplainDetails />}
                  />
                </Route>
                {/*Complaints Configuration Routes */}
                <Route
                  path={`${MainRoutes.COMPLAINTCONFIGURATION}`}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.COMPLAINT_CONFIGURATION}
                    >
                      <ComplaintConfiguration />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`${MainRoutes.COMPLAINTLEVELS}`}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.COMPLAINT_LEVELS}
                    >
                      <LevelData />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`${MainRoutes.ESCALATIONDETAILS}/:id`}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.COMPLAINT_CONFIGURATION}
                    >
                      <EscalationDetails />
                    </ProtectedRoute>
                  }
                />
                {/*Complaints Deadline Approval Routes */}
                <Route
                  path={`${MainRoutes.DEADLINEAPPROVAL}`}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.DEADLINE_APPROVAL}
                    >
                      <ComplaintDeadlineApproval />
                    </ProtectedRoute>
                  }
                />
                {/*Complaints Complaint Request Approval Routes */}
                <Route
                  path={`${MainRoutes.COMPLAINT_REQUESTS}`}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.COMPLAINT_REQUESTS}
                    >
                      <ComplaintRequestApproval />
                    </ProtectedRoute>
                  }
                />
                {/*Vendor Rating Routes */}
                <Route
                  path={`${MainRoutes.RATING}`}
                  element={
                    <ProtectedRoute requiredPermission={MODULE_LABELS.RATING}>
                      <VendorRating />
                    </ProtectedRoute>
                  }
                />
                {/* MIS Dashboard  Routes */}
                <Route
                  path={`${MainRoutes.MIS_COMPLAINT_DASHBOARD}`}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.MIS_COMPLAINT_DASHBOARD}
                    >
                      <MisComplaintDashboard />     
                    </ProtectedRoute>
                  }
                />
                {/* <Route
                  path={`${MainRoutes.MIS_LOCATION_ANALYTICS}`}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.MIS_LOCATION_ANALYTICS}
                    >
                      <MisLocationAnalytics />
                    </ProtectedRoute>
                  }
                /> */}
                <Route
                  path={`${MainRoutes.MIS_ASSIGNED_COMPLAINTS_REPORT}`}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.MIS_ASSIGNED_COMPLAINTS_REPORT}
                    >
                      <MisAssignedReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`${MainRoutes.MIS_CLOSED_COMPLAINTS_REPORT}`}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.MIS_CLOSED_COMPLAINTS_REPORT}
                    >
                      <MisClosedReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`${MainRoutes.MIS_CANCELLED_COMPLAINTS_REPORT}`}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.MIS_CANCELLED_COMPLAINTS_REPORT}
                    >
                      <MisCancelledReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`${MainRoutes.MIS_VENDOR_ANALYTICS}`}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.MIS_VENDOR_ANALYTICS}
                    >
                      <MisVendorAnalytics />
                    </ProtectedRoute>
                  } 
                />
                  <Route
                  path={`${MainRoutes.MIS_SETTINGS}`}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.MIS_SETTINGS}
                    >
                      <MisSettings />
                    </ProtectedRoute>
                  } 
                />
           
                {/* <Route
                  path={`${MainRoutes.MISDETAILEDVIEW}`}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.MIS_DETAILED_VIEW}
                    >
                      <MisDetailedView />
                    </ProtectedRoute>
                  }
                /> */}

                {/* Notice  Routes */}
                <Route
                  path={`${MainRoutes.NOTICE}`}
                  element={
                    <ProtectedRoute requiredPermission={MODULE_LABELS.NOTICE}>
                      <Notice />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`${MainRoutes.NOTICEDETAILS}/:id`}
                  element={
                    <ProtectedRoute requiredPermission={MODULE_LABELS.NOTICE}>
                      <NoticeDetails />
                    </ProtectedRoute>
                  }
                />
                {/* Token Routes */}
                <Route
                  path={`${MainRoutes.TOKEN}`}
                  element={
                    <ProtectedRoute requiredPermission={MODULE_LABELS.TOKEN}>
                      <Token />
                    </ProtectedRoute>
                  }
                />
                {/* UNAUTHORIZED ROUTE */}
                <Route path="*" element={<Unauthorized />} />

                {/*help-page route*/}
                <Route
                  path={`${MainRoutes.HELP_CENTER}`}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.HELP_CENTER}
                    >
                      <HelpCenter />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path={`${MainRoutes.ADDFAQ}`}
                  element={
                    <ProtectedRoute requiredPermission={MODULE_LABELS.ADD_FAQ}>
                      <AddFaq />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`${MainRoutes.FAQDETAILS}/:id`}
                  element={
                    <ProtectedRoute
                      requiredPermission={MODULE_LABELS.HELP_CENTER}
                    >
                      <FaqDetails />
                    </ProtectedRoute>
                  }
                />
                  <Route
                    path={`${MainRoutes.CONTACTBOOK}`}
                    element={
                      <ProtectedRoute requiredPermission={MODULE_LABELS.DIRECTORY}>
                        <ContactBook />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`${MainRoutes.CONTACTBOOKSUBCATEGORY}/:id`}
                    element={
                      <ProtectedRoute requiredPermission={MODULE_LABELS.DIRECTORY}>
                        <ContactBookSubcategory />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`${MainRoutes.CONTACT}/:categoryId/:id`}
                    element={
                      <ProtectedRoute requiredPermission={MODULE_LABELS.DIRECTORY}>
                        <Contact />
                      </ProtectedRoute>
                    }
                  />
                     <Route
                    path={`${MainRoutes.CONTACT}/:id`}
                    element={
                      <ProtectedRoute requiredPermission={MODULE_LABELS.DIRECTORY}>
                        <Contact />
                      </ProtectedRoute>
                    }
                  />
              </Route>
            )}
          </Routes>
        </Suspense>
        <Toaster
          position="top-center"
          reverseOrder={false}
          toastOptions={{
            className: "",
            duration: 5000,
          }}
        />
      </div>
    </div>
  );
};

export default App;
